import React from "react";
import { ArrowRight, Download, Github, Linkedin, MapPin, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { profile, heroStats } from "../data/mock";

const Hero = () => {
	const handleDownload = () => {
		window.alert("Resume download will be wired to a real PDF.");
	};

	return (
		<section id="top" className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden">
			<div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
			<div className="absolute inset-0 bg-radial-teal pointer-events-none" />
			<div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-teal-500/10 blur-3xl pointer-events-none" />
			<div className="absolute top-1/3 -left-40 w-[420px] h-[420px] rounded-full bg-teal-700/10 blur-3xl pointer-events-none" />

			<div className="relative max-w-7xl mx-auto px-6 lg:px-10">
				<div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
					{/* Left column — text */}
					<div className="lg:col-span-7">
						<div className="flex items-center gap-2 text-xs font-mono text-teal-300/90 mb-6 fade-up">
							<span className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-teal-500/30 bg-teal-500/5">
								<Sparkles className="w-3.5 h-3.5" />
								{profile.available}
							</span>
						</div>

						<h1 className="fade-up font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight">
							<span className="text-slate-300">Hello, </span>
							<br />
							<span className="text-slate-300">I&apos;m </span>
							<span className="bg-gradient-to-r from-teal-200 via-teal-400 to-teal-600 bg-clip-text text-transparent text-glow">{profile.name}</span>
							<span className="text-slate-100">.</span>
							<br />
							<br />
							<span className="text-slate-300">I build</span>

							<span className="text-teal-300"> Cool Stuff</span>
							<span className="text-slate-300">.</span>
						</h1>

						<p className="fade-up mt-7 max-w-2xl text-lg lg:text-xl text-slate-300 leading-relaxed">{profile.tagline}</p>

						<div className="fade-up mt-8 flex flex-wrap items-center gap-3">
							<Button onClick={handleDownload} size="lg" className="bg-teal-400 text-[#04100f] hover:bg-teal-300 font-semibold h-12 px-6 shadow-[0_0_40px_-8px_rgba(45,212,191,0.5)]">
								<Download className="w-5 h-5 mr-2" />
								Download Resume
							</Button>
							<Button asChild variant="outline" size="lg" className="h-12 px-6 border-teal-500/40 text-teal-200 hover:text-teal-100 hover:bg-teal-500/10 bg-transparent">
								<a href="#projects">
									View Projects
									<ArrowRight className="w-5 h-5 ml-2" />
								</a>
							</Button>
							<div className="flex items-center gap-2 ml-1">
								<a href={profile.linkedin} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full border border-teal-500/30 text-slate-300 hover:text-teal-300 hover:border-teal-400 flex items-center justify-center transition-colors" aria-label="LinkedIn">
									<Linkedin className="w-4 h-4" />
								</a>
								<a href={profile.github} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full border border-teal-500/30 text-slate-300 hover:text-teal-300 hover:border-teal-400 flex items-center justify-center transition-colors" aria-label="GitHub">
									<Github className="w-4 h-4" />
								</a>
							</div>
						</div>

						<div className="fade-up mt-6 flex items-center gap-2 text-sm text-slate-400">
							<MapPin className="w-4 h-4 text-teal-400" />
							{profile.location} · {profile.graduating}
						</div>
					</div>

					{/* Right column — photo */}
					<div className="lg:col-span-5 fade-up">
						<div className="relative mx-auto w-full max-w-md">
							{/* Decorative glow */}
							<div className="absolute -inset-6 bg-gradient-to-tr from-teal-500/30 via-teal-400/10 to-transparent blur-2xl rounded-[2rem] pointer-events-none" />
							{/* Subtle teal corner accent */}
							<div className="absolute -top-3 -left-3 w-20 h-20 border-t-2 border-l-2 border-teal-400/60 rounded-tl-2xl pointer-events-none" />
							<div className="absolute -bottom-3 -right-3 w-20 h-20 border-b-2 border-r-2 border-teal-400/60 rounded-br-2xl pointer-events-none" />

							<div className="relative rounded-2xl overflow-hidden border border-teal-500/25 bg-[#08201d] shadow-[0_30px_80px_-20px_rgba(20,184,166,0.35)]">
								<img src={profile.photoUrl} alt={profile.name} className="w-full h-[440px] sm:h-[500px] object-cover" />
								<div className="absolute inset-0 bg-gradient-to-t from-[#04100f]/70 via-transparent to-transparent pointer-events-none" />
								<div className="absolute bottom-0 inset-x-0 p-5 flex items-end justify-between">
									<div>
										<div className="text-xs font-mono uppercase tracking-[0.2em] text-teal-300/90">{profile.role}</div>
										<div className="font-display text-lg font-semibold text-slate-100">{profile.graduating}</div>
									</div>
									<div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-teal-400/15 border border-teal-400/40 text-[11px] font-mono text-teal-200">
										<span className="w-1.5 h-1.5 rounded-full bg-teal-300 animate-pulse" />
										Open to work
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* Stats — full width below */}
				<div className="fade-up mt-16 grid grid-cols-2 sm:grid-cols-4 gap-4">
					{heroStats.map((s) => (
						<div key={s.label} className="rounded-xl border border-teal-500/15 bg-[#08201d]/60 p-5 hover:border-teal-400/40 hover:bg-[#0a2a26]/60 transition-colors">
							<div className="font-display text-3xl font-bold text-teal-300">{s.value}</div>
							<div className="text-xs uppercase tracking-wider text-slate-400 mt-1">{s.label}</div>
						</div>
					))}
				</div>
			</div>
		</section>
	);
};

export default Hero;
